import { Injectable, inject, signal } from '@angular/core';
import { PlanPrice } from './checkout.service';
import { CheckoutProviderService } from './checkout-provider.service';
import { StorageService } from './storage.service';
import { formatAmount, monthsFreeOf, yearlySavingOf } from './pricing.util';

/**
 * Locale de mise en forme, déduite de la langue de l'app.
 *
 * `en-IE` et non `en-US` : c'est une locale anglophone de la zone euro, qui
 * écrit « €3.99 » là où `en-US` écrirait « €3.99 » aussi mais avec des règles
 * de séparateurs américaines sur les grands nombres.
 */
const LOCALES: Record<string, string> = {
  FR: 'fr-BE',
  NL: 'nl-BE',
  DE: 'de-DE',
  IT: 'it-IT',
  ES: 'es-ES',
  EN: 'en-IE',
};

/**
 * LA source des montants affichés.
 *
 * Règle absolue : aucun prix n'est écrit dans l'app, ni dans un libellé i18n ni
 * dans un composant. Les libellés ne portent plus que le gabarit
 * (« {{price}}/mois ») et le montant vient du fournisseur de paiement : Stripe
 * sur le web, le magasin sur mobile.
 *
 * Pourquoi c'est indispensable : le prix d'un achat intégré varie par pays
 * (3,99 € en Belgique, un autre palier en France, une autre devise ailleurs).
 * Afficher un montant que le magasin ne facture pas trompe l'utilisateur, et
 * c'est un motif de refus explicite chez Apple.
 *
 * Corollaire : « ≈ 2 mois offerts » et « soit 3,33 €/mois » sont CALCULÉS. Avec
 * 5 €/mois et 39,99 €/an, l'économie n'est plus de 2 mois mais de 4 — écrite en
 * dur, la mention serait fausse dans ce pays.
 */
/**
 * Délai au-delà duquel les prix sont tenus pour indisponibles. Un magasin
 * répond en une ou deux secondes ; au-delà, mieux vaut le dire et proposer de
 * réessayer que laisser des tirets à l'écran.
 */
export const PRICES_TIMEOUT_MS = 10_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('prices timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

@Injectable({ providedIn: 'root' })
export class PricingService {
  // La fabrique tranche à l'exécution : achat intégré dans l'app native, Stripe
  // partout ailleurs. Ce service ignore lequel a répondu.
  private readonly checkout = inject(CheckoutProviderService);
  private readonly storage = inject(StorageService);

  /** Prix rapportés par le fournisseur. Vide tant que rien n'est chargé. */
  readonly prices = signal<PlanPrice[]>([]);

  /** Une réponse est arrivée (même vide) : « aucun prix » devient une info. */
  readonly loaded = signal(false);

  /** Une demande est en cours (premier chargement ou nouvel essai). */
  readonly loading = signal(false);

  /**
   * La dernière demande a échoué ou n'a pas répondu à temps. À distinguer
   * d'une réponse vide : « Stripe n'est pas configuré ici » n'appelle pas le
   * même message que « le réseau a lâché ».
   */
  readonly failed = signal(false);

  /** Chargement en cours, pour ne pas le lancer deux fois. */
  private inFlight?: Promise<void>;

  /**
   * Charge les prix une seule fois. Un échec laisse la liste vide : mieux vaut
   * une formule sans montant qu'un montant inventé.
   *
   * L'attente est bornée : un magasin muet (canal natif sans réponse) ou une
   * requête Stripe pendue laissaient l'écran sur « —/an » indéfiniment.
   */
  async load(): Promise<void> {
    if (this.prices().length > 0) return;
    if (!this.inFlight) {
      this.loading.set(true);
      this.failed.set(false);
      this.inFlight = withTimeout(this.checkout.getPrices(), PRICES_TIMEOUT_MS)
        .then((prices) => this.prices.set(prices))
        .catch(() => {
          this.prices.set([]);
          this.failed.set(true);
        })
        .finally(() => {
          this.inFlight = undefined;
          this.loading.set(false);
          this.loaded.set(true);
        });
    }
    return this.inFlight;
  }

  /** Le fournisseur a-t-il donné un montant pour cette formule ? */
  hasPrice(plan: string): boolean {
    return Boolean(this.of(plan));
  }

  /**
   * Montant à afficher, ou un tiret. Jamais un prix approché.
   *
   * Quand le magasin fournit sa propre chaîne (`priceString`), c'est ELLE qui
   * gagne : il l'a mise en forme selon les conventions du pays du compte, et la
   * recalculer rouvrirait l'écart entre le prix affiché et le prix facturé.
   */
  label(plan: string): string {
    const price = this.of(plan);
    if (!price) return '—';
    return price.priceString ?? this.format(price.amount, price.currency);
  }

  /**
   * Économie de la formule annuelle, en mois. `null` quand elle n'est pas
   * calculable — le badge disparaît alors, plutôt que d'annoncer un chiffre faux.
   */
  monthsFree(): number | null {
    return monthsFreeOf(this.of('MONTHLY'), this.of('YEARLY'));
  }

  /**
   * Économie de l'annuel sur douze mois, formatée à l'unité (« 8 € »), ou
   * `null` si elle n'est pas calculable : la mention disparaît alors.
   */
  yearlySaving(): string | null {
    const yearly = this.of('YEARLY');
    const saving = yearlySavingOf(this.of('MONTHLY'), yearly);
    return saving && yearly ? this.format(saving, yearly.currency, true) : null;
  }

  /** Jours d'essai offerts par le MAGASIN sur cette formule, ou 0. */
  storeTrialDays(plan: string): number {
    return this.of(plan)?.trialDays ?? 0;
  }

  /** Équivalent mensuel de la formule annuelle, formaté. */
  monthlyEquivalent(): string | null {
    const yearly = this.of('YEARLY');
    return yearly ? this.format(yearly.amount / 12, yearly.currency) : null;
  }

  private of(plan: string): PlanPrice | undefined {
    return this.prices().find((price) => price.plan === plan);
  }

  private format(amount: number, currency: string, wholeUnits = false): string {
    const lang = (this.storage.getCurrentLanguage() ?? 'FR').toUpperCase();
    return formatAmount(amount, currency, LOCALES[lang] ?? 'fr-BE', wholeUnits);
  }
}
